
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { 
  DropdownMenu, 
  DropdownMenuTrigger, 
  DropdownMenuContent, 
  DropdownMenuItem 
} from '@/components/ui/dropdown-menu';
import { ChevronDown } from 'lucide-react';

interface HeaderProps {
  variant?: 'default' | 'light';
}

const Header = ({ variant = 'default' }: HeaderProps) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isResourcesExpanded, setIsResourcesExpanded] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 10) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Determine text colors based on variant and scroll state
  const isLightMode = variant === 'light' && !isScrolled;
  const textColor = isLightMode ? 'text-white' : 'text-fordje-navy-light';
  const logoTextColor = isLightMode ? 'text-white' : 'text-fordje-navy';
  const hoverColor = isLightMode ? 'hover:text-white/80' : 'hover:text-fordje-blue';
  const mobileMenuColor = isLightMode ? 'text-white' : 'text-fordje-navy';

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-md py-3' : 'bg-transparent py-5'}`}>
      <div className="container-custom">
        <div className="flex justify-between items-center gap-4">
          <Link to="/" className="flex items-center gap-2 flex-shrink-0">
            <img 
              src="/lovable-uploads/595a50f5-c631-4403-8464-cad23f811cfe.png" 
              alt="Fordje Logo" 
              className="h-8 w-8 md:h-10 md:w-10"
            />
            <span className={`text-xl md:text-2xl font-bold font-ubuntu ${logoTextColor}`}>Fordje</span>
          </Link>
          
          <div className="hidden lg:flex items-center gap-3 xl:gap-4">
            <nav className="flex items-center gap-4 xl:gap-6">
              <Link to="/" className={`${textColor} ${hoverColor} transition-colors`}>Home</Link>
              <Link to="/product" className={`${textColor} ${hoverColor} transition-colors`}>Product</Link>
              <Link to="/about" className={`${textColor} ${hoverColor} transition-colors`}>About</Link>
              
              {/* Resources Dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger className={`flex items-center ${textColor} ${hoverColor} transition-colors focus:outline-none`}>
                  <span>Resources</span>
                  <ChevronDown className="ml-1 h-4 w-4" />
                </DropdownMenuTrigger>
                <DropdownMenuContent className="bg-white">
                  <DropdownMenuItem asChild>
                    <Link to="/blog" className="w-full">Insights</Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/residential-clean-energy-guide" className="w-full">Residential solar &amp; storage guide</Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/commercial-clean-energy-guide" className="w-full">Commercial clean energy guide</Link>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </nav>
            <div className="flex items-center gap-2">
              <Link to="https://app.fordje.com">
                <Button variant="outline" className={`text-sm px-3 ${isLightMode ? 'border-white/70 text-white bg-white/10 hover:bg-white/20' : 'border-fordje-blue text-fordje-blue hover:bg-fordje-blue/10'}`}>
                  Login
                </Button>
              </Link>
              <Link to="/request-demo">
                <Button className="bg-fordje-blue hover:bg-fordje-blue-dark text-sm px-3">Demo</Button>
              </Link>
            </div>
          </div>
          
          <button 
            className={`lg:hidden ${mobileMenuColor}`}
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
        </div>
        
        {/* Mobile menu with background */}
        {isMobileMenuOpen && (
          <div className={`lg:hidden pt-4 pb-2 bg-white ${!isScrolled ? 'mt-5 shadow-md rounded-lg' : ''}`}>
            <nav className="flex flex-col space-y-4">
              <Link to="/" className="text-fordje-navy-light hover:text-fordje-blue transition-colors px-4">Home</Link>
              <Link to="/product" className="text-fordje-navy-light hover:text-fordje-blue transition-colors px-4">Product</Link>
              <Link to="/about" className="text-fordje-navy-light hover:text-fordje-blue transition-colors px-4">About</Link>
              
              {/* Resources mobile dropdown */}
              <div className="px-4">
                <button
                  onClick={(e) => { e.preventDefault(); setIsResourcesExpanded(!isResourcesExpanded); }}
                  className="flex items-center justify-between w-full text-left text-fordje-navy-light hover:text-fordje-blue transition-colors"
                >
                  <span>Resources</span>
                  <ChevronDown className={`h-4 w-4 transition-transform ${isResourcesExpanded ? 'rotate-180' : ''}`} />
                </button>
                
                {isResourcesExpanded && (
                  <div className="pl-4 mt-2 space-y-2">
                    <Link to="/blog" className="block text-fordje-navy-light hover:text-fordje-blue transition-colors">Insights</Link>
                    <Link to="/residential-clean-energy-guide" className="block text-fordje-navy-light hover:text-fordje-blue transition-colors">Residential solar &amp; storage guide</Link>
                    <Link to="/commercial-clean-energy-guide" className="block text-fordje-navy-light hover:text-fordje-blue transition-colors">Commercial clean energy guide</Link>
                  </div>
                )}
              </div>
              
              <Link to="https://app.fordje.com" className="text-fordje-navy-light hover:text-fordje-blue transition-colors px-4">
                Login
              </Link>
              <div className="px-4 pt-2">
                <Link to="/request-demo">
                  <Button className="bg-fordje-blue hover:bg-fordje-blue-dark w-full">Book a Demo</Button>
                </Link>
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
